import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RecipientPreviewModal } from '../broadcasts';
import type { AudienceScope } from '../../types/broadcast';
import type { SurveyLinkMode } from '../../types/survey';
import { previewSurveyAudience } from './api';

// Phase 6B B+ — the broadcast-style audience summary, reused by the compose
// Audience tab + the Send tab. Four counts (Accounts / Contacts / Email- /
// SMS-reachable) + a "Preview audience" link opening the SHARED
// RecipientPreviewModal (read-only — surveys send from the Send tab). For
// per_location, a clarifying note: many contacts get the link, one response per
// location. Self-contained (own debounced count query + modal); React Query
// dedupes the ['survey-audience', scope] key across instances.

function hasBrand(scope: AudienceScope): boolean {
  return scope.brandSelection === 'all' || scope.brandSelection.brandAccountIds.length > 0;
}

export function SurveyAudienceSummary({
  scope,
  linkMode,
}: {
  scope: AudienceScope;
  linkMode: SurveyLinkMode;
}) {
  const scopeKey = JSON.stringify(scope);
  const [debounced, setDebounced] = useState<AudienceScope>(scope);
  const [showRecipients, setShowRecipients] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(scope), 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopeKey captures scope
  }, [scopeKey]);

  const preview = useQuery({
    queryKey: ['survey-audience', debounced],
    queryFn: () => previewSurveyAudience(debounced),
    enabled: hasBrand(debounced),
  });

  const locationNote =
    linkMode === 'per_location' && preview.data
      ? `${preview.data.totalAccounts} ${preview.data.totalAccounts === 1 ? 'location' : 'locations'} will receive the link — the first responder submits for their location.`
      : null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="px-4 py-3 text-sm">
        {!hasBrand(scope) ? (
          <p className="text-slate-500">Choose a brand to preview the audience.</p>
        ) : preview.isLoading ? (
          <p className="text-slate-500">Counting audience…</p>
        ) : preview.error ? (
          <p className="text-red-600">Couldn't count the audience.</p>
        ) : preview.data ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
            <Counter label="Accounts" value={preview.data.totalAccounts} />
            <Counter label="Contacts" value={preview.data.totalContacts} />
            <Counter label="Email-reachable" value={preview.data.emailReachable} />
            <Counter label="SMS-reachable" value={preview.data.smsReachable} />
          </dl>
        ) : null}
        {locationNote && <p className="mt-2 text-xs text-slate-500">{locationNote}</p>}
      </div>
      {preview.data && preview.data.totalContacts > 0 && (
        <div className="border-t border-slate-100 px-4 py-2">
          <button
            type="button"
            onClick={() => setShowRecipients(true)}
            className="text-sm font-medium text-kyck-700 hover:text-kyck-800"
          >
            Preview audience →
          </button>
        </div>
      )}

      {showRecipients && (
        <RecipientPreviewModal
          queryKey={['survey-audience-details', debounced]}
          fetchDetails={() => previewSurveyAudience(debounced, true)}
          onClose={() => setShowRecipients(false)}
          subtitle={locationNote ?? undefined}
        />
      )}
    </div>
  );
}

function Counter({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-semibold text-slate-900">{value}</dd>
    </div>
  );
}
